import { isMarkupOverdue } from "@utils/markups-utils";
import { useMemo, useState } from "react";
import { useUpdateDueDate } from "@pages/project-details/project-markups/due-date/markup-due-date-utils";
import { FaroTextButtonDatePicker } from "@components/common/faro-text-button-date-picker/faro-text-button-date-picker";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { markupsTemplateIdsSelector } from "@store/markups/markups-selector";
import { assert } from "@faro-lotv/foundation";
import { isMutationDateTimeMarkupField } from "@pages/project-details/project-markups/markup-guards";
import { currentUserSelector } from "@store/user/user-selector";
import { removeIElements, setIElements } from "@store/i-elements/i-elements-slice-thunk";

/** Renders the markup due date */
export function MarkupDueDate({
  markup,
  isSidePanelOpen = false
}) {
  const templateIds = useAppSelector(markupsTemplateIdsSelector);
  const currentUser = useAppSelector(currentUserSelector);
  assert(templateIds, "Expected project to have an advanced markup template");
  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    projectId,
    setIsMarkupUpdating,
    updateSelectedMarkupId,
    hasPermissionToEditMarkup
  } = useMarkupContext();
  const {
    onDueDateChange,
    removeDueDate
  } = useUpdateDueDate({
    projectId
  });
  const dispatch = useAppDispatch();
  const markupDate = useMemo(() => {
    return markup.dueDate ? new Date(markup.dueDate.value) : null;
  }, [markup.dueDate]);

  /** Applies the due date change to the markup in the store */
  function processMarkupChanges(action) {
    if (action) {
      void dispatch(action);
    }
    setIsLoading(false);
    setIsMarkupUpdating(false);
  }

  /** Call the appropriate function depending whether the date is removed or changed */
  async function onDateConfirmed(updatedDate) {
    if (!templateIds) {
      return;
    }
    closeDueDate();
    setIsLoading(true);
    setIsMarkupUpdating(true);
    if (updatedDate) {
      const mutation = await onDueDateChange(updatedDate, markup, templateIds.dueDateTemplateId);
      if (!mutation || !currentUser) {
        return;
      }
      const currentDueDate = markup.dueDate;
      if ("newElement" in mutation) {
        const newElement = mutation.newElement;
        const newItem = {
          ...newElement,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          root_Id: newElement.rootId,
          createdBy: currentUser.identity,
          createdAt: mutation.createdAt,
          modifiedBy: currentUser.identity,
          modifiedAt: mutation.createdAt
        };
        processMarkupChanges(setIElements([newItem]));
        return;
      }
      if (!currentDueDate) {
        throw new Error("Due date is required but was undefined.");
      }
      if (isMutationDateTimeMarkupField(mutation)) {
        const newDueDate = {
          ...currentDueDate,
          value: mutation.dateTime
        };
        processMarkupChanges(setIElements([newDueDate]));
      }
    } else {
      const dueDate = markup.dueDate;
      const isRemoved = await removeDueDate(markup);
      processMarkupChanges(isRemoved && dueDate ? removeIElements([dueDate]) : undefined);
    }
  }

  /**
   * It will close the date picker and remove the selected markup id
   */
  function closeDueDate() {
    setAnchorEl(null);
    updateSelectedMarkupId(undefined);
  }

  /**
   * Updates the anchor element and sets the selected markup ID.
   *
   * @param event - The mouse event triggered by clicking the button.
   */
  function updateAnchorEl(event) {
    setAnchorEl(event.currentTarget);
    updateSelectedMarkupId(markup.id);
  }
  return <FaroTextButtonDatePicker isDisabled={!hasPermissionToEditMarkup} isLoading={isLoading} isClearable={true} anchorEl={anchorEl} closeDatePicker={closeDueDate} onDateConfirmed={onDateConfirmed} updateAnchorEl={updateAnchorEl} value={markupDate} buttonSx={{
    color: isMarkupOverdue(markup) ? "error.main" : "inherit",
    fontWeight: isMarkupOverdue(markup) ? "600" : "normal",
    pl: isSidePanelOpen ? "1px" : "8px",
    pr: "8px",
    "&.Mui-disabled": {
      color: isMarkupOverdue(markup) ? "error.main" : "inherit"
    }
  }} />;
}